import { GetterTree } from 'vuex';

import RootState from '@/store/root_state';
import { MicrocertificateState } from './state';
import { MicrocertificateDiscipline, UserMicrocertificate } from './types';
import { UserMicrocertificateDTO } from '@/services/undergrad/pegasus-pepe-legal/microcertificates.service';
import { UndergradDiscipline } from '@/models/undergrad/undergrad-discipline.model';
import { DisciplineEvaluationAndDeadlines } from '@/models/evaluation-and-deadlines/evaluation-and-deadlines.model';

const getters: GetterTree<MicrocertificateState, RootState> = {
  userMicrocertificates: (state, _getters, _rootState, rootGetters): UserMicrocertificate[] => {
    return state.microcertificates.map((microcertificate: UserMicrocertificateDTO) => {
      if (microcertificate.disciplineId) {
        return {
          ...microcertificate,
          progress: rootGetters.getDisciplineProgress(
            rootGetters.disciplineById(microcertificate.disciplineId),
          ),
        };
      }

      return {
        ...microcertificate,
        progress: rootGetters.getModuleProgress(microcertificate.submoduleId),
      };
    });
  },
  getMicrocertificateDisciplines:
    (_state, _getters, _rootState, rootGetters) =>
    (userMicrocertificate: UserMicrocertificate): MicrocertificateDiscipline[] => {
      if (!userMicrocertificate.submoduleId) return [];

      const moduleDisciplines: UndergradDiscipline[] =
        rootGetters.undergradDisciplines?.filter(
          (discipline: UndergradDiscipline) =>
            discipline.submoduleId === userMicrocertificate.submoduleId,
        ) ?? [];

      const disciplines = moduleDisciplines.map((discipline: UndergradDiscipline) => {
        const evaluation: DisciplineEvaluationAndDeadlines =
          rootGetters.getEvaluationAndDeadlines?.find(
            (evalAndDeadlines: DisciplineEvaluationAndDeadlines) =>
              evalAndDeadlines.disciplineInstanceId === discipline.disciplineInstanceId,
          );

        const { id, name } = discipline;
        const grade = evaluation?.grade ?? 0;
        const progress =
          rootGetters.getDisciplineProgress(rootGetters.disciplineById(discipline.id)) ?? 0;

        return { id, name, grade, progress };
      });

      return disciplines;
    },
  isGeneratingMicroCertificate: (state) => {
    return state.isGenerating;
  },
  getMicrocertificateForSubmodule:
    (_state, moduleGetters) =>
    (submoduleId: string): UserMicrocertificate | null => {
      return (
        moduleGetters.userMicrocertificates.find(
          (microcertificate: UserMicrocertificate) => microcertificate.submoduleId === submoduleId,
        ) ?? null
      );
    },
  getMicrocertificateForDiscipline:
    (_state, moduleGetters) =>
    (disciplineId: string): UserMicrocertificate | null => {
      return (
        moduleGetters.userMicrocertificates.find(
          (microcertificate: UserMicrocertificate) =>
            microcertificate.disciplineId === disciplineId,
        ) ?? null
      );
    },
  getCurrentMicrocertificateFiles: (state) => {
    return state.currentMicrocertificateFiles;
  },
  getCurrentMicrocertificateHash: (state) => {
    return state.currentMicrocertificateHash;
  },
  displayingMicrocertificates: (state, moduleGetters): UserMicrocertificate[] => {
    return moduleGetters.userMicrocertificates.filter(
      ({ microcertificateId }: UserMicrocertificate) =>
        state.displayingMicrocertificates.includes(microcertificateId),
    );
  },
  acceptsMicrocertificateAutoPopup: (state): boolean => state.acceptsMicrocertificateAutoPopup,
};

export default getters;
